import { Navigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { TEMPLATES, TEMPLATE_ORDER, OWNER_SECTION } from '../lib/dashboardTemplates.js';

// SUPER-ADMIN — a read-only catalog of the dashboard templates. Each template is a
// named nav layout (which sidebar sections/items a tenant sees). We configure a
// tenant by setting features.template = '<key>' instead of hand-removing menu items;
// this page shows the owner what each template entails so they can pick the right one.
// Owner-gated client-side (redirect) — it's a platform tool, not a tenant view.

export default function Templates() {
  const { isOwner } = useAuth();
  if (!isOwner) return <Navigate to="/" replace />;

  return (
    <div className="px-6 md:px-10 py-8 max-w-4xl">
      <PageHeader
        title="Dashboard templates"
        subtitle="Named sidebar layouts. Assign one to a tenant with features.template = the key — no more hand-removing menu items per account."
      />

      <div className="grid gap-4 md:grid-cols-2">
        {TEMPLATE_ORDER.map((key) => {
          const t = TEMPLATES[key];
          if (!t) return null;
          const sections = t.includesOwnerAdmin ? [...t.sections, OWNER_SECTION] : t.sections;
          const itemCount = sections.reduce((n, s) => n + s.items.length, 0);
          return (
            <section key={key} className="card space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-stone-100 font-semibold">{t.label}</h3>
                  <code className="text-[11px] text-alfred-gold/80">{key}</code>
                </div>
                <span className="badge bg-stone-800 text-stone-400 border border-stone-700 text-[10px] shrink-0">
                  {itemCount} items
                </span>
              </div>

              <p className="text-sm text-stone-400">{t.description}</p>

              <div className="space-y-2 pt-1">
                {sections.map((s) => (
                  <div key={s.label}>
                    <div className="field-label text-[11px] uppercase tracking-wide text-stone-500">{s.label}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {s.items.map((it) => {
                        const Icon = it.icon;
                        return (
                          <span key={it.to + it.label}
                            className="inline-flex items-center gap-1 rounded-md border border-stone-700 bg-stone-800/60 px-2 py-0.5 text-xs text-stone-300">
                            {Icon && <Icon size={12} />} {it.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {t.includesOwnerAdmin && (
                <p className="text-[11px] text-stone-500">Includes the owner-only Admin section.</p>
              )}
            </section>
          );
        })}
      </div>

      <p className="mt-6 text-xs text-stone-500">
        Back-compat: a tenant with the legacy <code>executiveAdmin</code> flag resolves to
        <code className="text-alfred-gold/80"> executive-assistant</code>, and
        <code> dashboard: 'tasks'</code> resolves to <code className="text-alfred-gold/80">small-business-owner</code>.
        Owners with no template see <code className="text-alfred-gold/80">super-admin</code>.
      </p>
    </div>
  );
}
